import React, {useState, useEffect} from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, TextField, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import Modal from '@mui/material/Modal';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import QuizIcon from '@mui/icons-material/Quiz';

import { adminGetTheoryStage, adminUpdateTheoryStage, adminDeleteTheoryStage } from "../../api/Api";
import { getAvailableCategories, getAvailableSlugs } from '../Academy/Theory/stageRegistry';
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminTheoryStages {
    token: string;
}

const fieldSx = (colors: any) => ({
    '& .MuiOutlinedInput-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        color: colors.text.primary,
        '& fieldset': { borderColor: colors.border.default },
        '&:hover fieldset': { borderColor: colors.border.accent },
        '&.Mui-focused fieldset': { borderColor: colors.accent.cyan, boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}` },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.08em',
        color: colors.text.secondary,
        '&.Mui-focused': { color: colors.accent.cyan },
    },
});

const AdminUpdateTheoryStage = (props: PropsAdminTheoryStages) => {
    const colors = useColors();
    const navigate = useNavigate();
    const { id } = useParams();
    const [openDelete, setOpenDelete] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [category, setCategory] = useState("");
    const [slug, setSlug] = useState("");
    const [title, setTitle] = useState("");
    const [orderIndex, setOrderIndex] = useState(0);
    const [points, setPoints] = useState(0);
    const [active, setActive] = useState(false);

    const token = props.token;

    const registryCategories = getAvailableCategories();
    // Union in the currently-saved value in case it predates content being
    // registered/removed in stageRegistry.ts, so editing doesn't lose data.
    const categoryOptions = category && !registryCategories.includes(category)
        ? [...registryCategories, category]
        : registryCategories;
    const registrySlugs = getAvailableSlugs(category);
    const slugOptions = slug && !registrySlugs.includes(slug)
        ? [...registrySlugs, slug]
        : registrySlugs;
    const slugHasContent = registrySlugs.includes(slug);

    const handleCategoryChange = (newCategory: string) => {
        setCategory(newCategory);
        setSlug(getAvailableSlugs(newCategory)[0] ?? "");
    };

    useEffect(() => {
        const fetchData = async () => {
            const data = await adminGetTheoryStage(token, id);
            if (!data) return;
            setCategory(data.category);
            setSlug(data.slug);
            setTitle(data.title);
            setOrderIndex(data.order_index);
            setPoints(data.points);
            setActive(data.active);
        }
        fetchData();
    }, []);

    const handleUpdate = async (e: any) => {
        e.preventDefault();
        setProcessing(true);
        const response = await adminUpdateTheoryStage(token, id, {
            category, slug, title, order_index: orderIndex, points, active
        });
        setProcessing(false);
        setMessage(response.msg);
    };

    const handleDelete = async (e: any) => {
        e.preventDefault();
        await adminDeleteTheoryStage(token, id);
        navigate("/admin/theory/stages");
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '32px' }}>
                <CyberpunkTitle
                    segments={[
                        { label: 'Theory Stages', to: '/admin/theory/stages' },
                        { label: title || 'Edit' },
                    ]}
                    mb="0"
                />
                <Link to={`/admin/theory/stage/${id}/questions`} style={{ textDecoration: 'none' }}>
                    <Button
                        variant="outlined"
                        startIcon={<QuizIcon />}
                        sx={{ borderColor: colors.accent.purple, color: colors.accent.purple, fontFamily: '"Share Tech Mono", monospace', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                    >
                        Questions
                    </Button>
                </Link>
            </Box>

            <GlowCard accentTop glowOnHover={false} sx={{ maxWidth: '600px' }}>
                <Box sx={{ padding: '32px' }}>
                    <Box component="form" onSubmit={handleUpdate} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <FormControl sx={fieldSx(colors)} required fullWidth>
                                <InputLabel id="update-theory-stage-category-label">Category</InputLabel>
                                <Select
                                    labelId="update-theory-stage-category-label"
                                    label="Category"
                                    value={category}
                                    onChange={(e) => handleCategoryChange(e.target.value as string)}
                                    autoFocus
                                >
                                    {categoryOptions.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={fieldSx(colors)} required fullWidth disabled={slugOptions.length === 0}>
                                <InputLabel id="update-theory-stage-slug-label">Slug</InputLabel>
                                <Select
                                    labelId="update-theory-stage-slug-label"
                                    label="Slug"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value as string)}
                                >
                                    {slugOptions.map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                                {!slugHasContent && (
                                    <FormHelperText sx={{ color: colors.accent.red }}>
                                        No matching content in stageRegistry.ts for "{category}" / "{slug}": this stage will render empty until fixed
                                    </FormHelperText>
                                )}
                            </FormControl>
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Order"
                                    type="number"
                                    value={orderIndex}
                                    onChange={(e) => setOrderIndex(Number(e.target.value))}
                                />
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Points"
                                    type="number"
                                    value={points}
                                    onChange={(e) => setPoints(Number(e.target.value))}
                                />
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent.cyan },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent.cyanDim },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem', letterSpacing: '0.08em', color: colors.text.secondary }}>
                                        Active
                                    </Typography>
                                }
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="outlined"
                                disabled={processing}
                                startIcon={processing ? <CircularProgress size={16} sx={{ color: colors.accent.cyan }} /> : <SaveIcon />}
                                sx={{
                                    borderColor: colors.accent.cyan,
                                    color: colors.accent.cyan,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: colors.accent.cyanGlow,
                                        borderColor: colors.accent.cyan,
                                        boxShadow: `0 0 20px ${colors.accent.cyan}33`,
                                    },
                                }}
                            >
                                {processing ? 'Saving...' : 'Save Changes'}
                            </Button>

                            <Box sx={{ height: '1px', backgroundColor: colors.border.subtle, margin: '4px 0' }} />

                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setOpenDelete(true)}
                                sx={{
                                    borderColor: colors.accent.red,
                                    color: colors.accent.red,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: colors.accent.redGlow,
                                        borderColor: colors.accent.red,
                                        boxShadow: `0 0 20px ${colors.accent.red}22`,
                                    },
                                }}
                            >
                                Delete Stage
                            </Button>

                            {message && (
                                <Box sx={{
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: colors.accent.cyanGlow,
                                    border: `1px solid ${colors.accent.cyan}44`,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: colors.accent.cyan,
                                    letterSpacing: '0.06em',
                                }}>
                                    {message}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </GlowCard>

            <Modal open={openDelete} onClose={() => setOpenDelete(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '400px' },
                    backgroundColor: colors.bg.secondary,
                    border: `1px solid ${colors.accent.red}55`,
                    borderRadius: '8px',
                    padding: '32px',
                    boxShadow: `0 0 40px ${colors.accent.red}22`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <WarningAmberIcon sx={{ color: colors.accent.red, fontSize: '1.5rem' }} />
                        <Typography sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: colors.text.primary }}>
                            Delete Stage
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem', color: colors.text.secondary, marginBottom: '24px', lineHeight: 1.6 }}>
                        Are you sure you want to delete <Box component="span" sx={{ color: colors.accent.cyan, fontWeight: 700 }}>{title}</Box>? This also removes access to its questions for users mid-track. This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '12px' }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setOpenDelete(false)}
                            sx={{ borderColor: colors.border.default, color: colors.text.secondary, fontFamily: '"Share Tech Mono", monospace', fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleDelete}
                            startIcon={<DeleteOutlineIcon />}
                            sx={{ borderColor: colors.accent.red, color: colors.accent.red, fontFamily: '"Share Tech Mono", monospace', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </PageContainer>
    );
};

export default AdminUpdateTheoryStage;
